import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Wedding } from '@guabi/shared';
import { Stat, StatStrip } from '@/components/ui/Stat';
import { money, percent } from '@/lib/format';
import { useWeddingSummary } from '@/features/shell/queries';
import { Onboarding } from './Onboarding';
import { useMembers } from '@/features/members/queries';

export function HomeTab() {
  const { t } = useTranslation();
  const { wedding } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const summary = useWeddingSummary(wedding.wedId);

  const s = summary.data;
  const members = useMembers(wedding.wsId);
  const currency = wedding.settings.currency;

  return (
    <section className="flex flex-col gap-6">
      <div>
        <p className="eyebrow">{t('nav.home')}</p>
        <h1 className="font-display text-display">{wedding.title}</h1>
      </div>

      {s && (
        <Onboarding
          summary={s}
          hasDate={Boolean(wedding.date)}
          hasPartner={(members.data?.members.length ?? 1) > 1}
        />
      )}

      <StatStrip>
        <Stat
          label={t('home.daysToGo')}
          value={s?.daysToWedding ?? '—'}
          sub={wedding.date ?? t('home.noDate')}
        />
        <Stat
          label={t('home.attending')}
          value={s?.guests.attending ?? 0}
          sub={`${s?.guests.invited ?? 0} ${t('home.invited')}${
            s ? ` · ${percent(s.guests.attending, s.guests.invited)}` : ''
          }`}
        />
        <Stat
          label={t('home.planned')}
          value={money(s?.budget.plannedCents ?? 0, currency)}
          sub={`${t('home.projected')} ${money(s?.budget.projectedCents ?? 0, currency)}`}
        />
        <Stat label={t('home.venuesShortlisted')} value={s?.venues.shortlisted ?? 0} />
        <Stat label={t('home.vendorsBooked')} value={s?.vendors.booked ?? 0} />
      </StatStrip>

      <hr className="rule" />

      {/*
        The next three things, not the whole checklist — that is the Timeline
        tab, and reproducing it here with worse typography helps nobody. Late
        first, because late is the only part of a checklist that is urgent.
      */}
      {s && s.tasks.open > 0 && (
        <section className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-section">{t('home.nextUp')}</h2>
            <Link
              to={`/app/w/${s.wedId}/timeline`}
              className="min-h-tap text-caption text-brass-text underline underline-offset-4"
            >
              {t('home.allTasks', { count: s.tasks.open })}
            </Link>
          </div>
          {s.tasks.next.length === 0 ? (
            <p className="text-body text-leaf-soft">{t('home.nothingDueSoon')}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {s.tasks.next.map((task) => (
                <li key={task.taskId} className="flex items-baseline justify-between gap-4 py-2">
                  <span className="text-body">{task.title}</span>
                  {task.dueDate && (
                    <span
                      className={`tabular shrink-0 text-caption ${
                        task.overdue ? 'text-status-declinedFg' : 'text-leaf-soft'
                      }`}
                    >
                      {task.dueDate}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <p className="max-w-prose text-body text-leaf-soft">{t('home.progress')}</p>
    </section>
  );
}
